import { IApplicationData } from 'src/interfaces/ApplicationData';
import { APIUserPreferencesResources, IUserPreferences } from 'src/interfaces/UserPreferences';
import { del, get, put } from 'src/utils/apiUtils';
import { debounce } from 'src/utils/debounceUtils';
import Validator from 'src/validators/Validator';
import { schema as subtitleStylesSchema } from 'src/validators/schemas/subtitleStyles';
import {
  DEFAULT_AUTOPLAY_ENABLED,
  DEFAULT_MARKER_PREVIEW_ENABLED,
  DEFAULT_PLAYBACK_RATE,
  DEFAULT_SUBTITLE_ENABLED,
  DEFAULT_SUBTITLE_LANGUAGE,
  DEFAULT_SUBTITLE_STYLE,
  DEFAULT_VOLUME_PERCENT,
  UPDATE_PREFERENCES_DEBOUNCE_TIME,
  USER_PREFERENCES_URL,
} from './constants';

type DeepPartial<T> = Partial<{ [P in keyof T]: DeepPartial<T[P]> }>;

class UserPreferencesService {
  private applicationData: IApplicationData;
  private readonly _subtitleValidator: Validator;

  constructor(applicationData: IApplicationData) {
    this.applicationData = applicationData;
    this._subtitleValidator = new Validator(subtitleStylesSchema);
  }

  generateRequestUrl(resource: APIUserPreferencesResources = APIUserPreferencesResources.default) {
    const { userCode, applicationKey } = this.applicationData;

    if (!userCode || !applicationKey) {
      throw new Error('UserId or applicationKey param is missing');
    }

    return `${USER_PREFERENCES_URL}/user-preferences/${userCode}${resource}?applicationCode=${applicationKey}`;
  }

  async fetchPreferences() {
    try {
      const result = await get(this.generateRequestUrl());
      return this.parsePreferences(result);
    } catch (error) {
      return this.parsePreferences({});
    }
  }

  private async _updatePreferences(preferences: DeepPartial<IUserPreferences>) {
    try {
      await put(this.generateRequestUrl(), preferences);
    } catch (error) {
      console.error(error);
    }
  }

  private async _resetSubtitleStyles() {
    try {
      await del(this.generateRequestUrl(APIUserPreferencesResources.subtitleStyle));
    } catch (error) {
      console.error(error);
    }
  }

  updatePreferences = debounce(this._updatePreferences, UPDATE_PREFERENCES_DEBOUNCE_TIME);
  resetSubtitleStyles = debounce(this._resetSubtitleStyles, UPDATE_PREFERENCES_DEBOUNCE_TIME);

  parsePreferences(preferences: Partial<IUserPreferences>): IUserPreferences {
    return {
      autoplayEnabled: preferences.autoplayEnabled ?? DEFAULT_AUTOPLAY_ENABLED,
      volumePercent: preferences.volumePercent ?? DEFAULT_VOLUME_PERCENT,
      markerPreviewEnabled: preferences.markerPreviewEnabled ?? DEFAULT_MARKER_PREVIEW_ENABLED,
      playbackRate: preferences.playbackRate ?? DEFAULT_PLAYBACK_RATE,
      subtitle: {
        language: preferences.subtitle?.language ?? DEFAULT_SUBTITLE_LANGUAGE,
        enabled: preferences.subtitle?.enabled ?? DEFAULT_SUBTITLE_ENABLED,
        style: this._subtitleValidator.sanitize({
          data: preferences.subtitle?.style,
          defaultValues: DEFAULT_SUBTITLE_STYLE,
        }),
      },
    };
  }
}

export default UserPreferencesService;
